import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The learner's own camera for the camera role play mode (user-facing "Video").
 *
 * Video only: the ElevenLabs SDK requests the microphone itself, so a refused camera is
 * a distinct, recoverable case and losing the camera device can never take the audio
 * down. The stream is rendered locally and never leaves the browser.
 *
 * States: `idle` (nothing held) → `requesting` → `active`, or `denied` when the browser
 * refuses. A track that ends mid-call (device unplugged, revoked, taken by another app)
 * moves to `lost` and is retried in the background with 1s/2s/4s/8s backoff until a
 * stream is obtained, the permission turns out revoked (`denied`), or `stop()` is called.
 */
export type SelfCameraState = 'idle' | 'requesting' | 'active' | 'denied' | 'lost'

export interface UseSelfCameraReturn {
  state: SelfCameraState
  /** The live camera stream, to be set as a `<video>` element's `srcObject`. */
  stream: MediaStream | null
  /** False while the camera toggle is off; the device stays held (see `setEnabled`). */
  isEnabled: boolean
  /** Ask for the camera. Resolves to `active` or `denied`; never throws. */
  request: () => Promise<'active' | 'denied'>
  /** Flip the track on or off without releasing the device, so turning it back on needs no new prompt. */
  setEnabled: (enabled: boolean) => void
  /** End every track and cancel pending retries. Safe to call more than once. */
  stop: () => void
}

const CAMERA_CONSTRAINTS: MediaStreamConstraints = { video: { facingMode: 'user' }, audio: false }

/** Retry delays after the camera is lost; the last one repeats. */
export const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000]

const isNotAllowed = (error: unknown): boolean => {
  const name = (error as { name?: string } | null)?.name
  return name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError'
}

const getUserMedia = (): Promise<MediaStream> => {
  const devices = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
  if (!devices?.getUserMedia) {
    return Promise.reject(Object.assign(new Error('Camera is not available in this context'), { name: 'NotAllowedError' }))
  }
  return devices.getUserMedia(CAMERA_CONSTRAINTS)
}

export function useSelfCamera(): UseSelfCameraReturn {
  const [state, setState] = useState<SelfCameraState>('idle')
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [isEnabled, setIsEnabled] = useState(true)

  const streamRef = useRef<MediaStream | null>(null)
  const enabledRef = useRef(true)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const retryAttemptRef = useRef(0)
  // Bumped by `stop()`: a request or retry that resolves afterwards belongs to a call that
  // has already ended and must release what it got instead of attaching it.
  const generationRef = useRef(0)

  const clearRetryTimer = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
  }

  const releaseStream = () => {
    const current = streamRef.current
    streamRef.current = null
    if (!current) return
    for (const track of current.getTracks()) {
      track.removeEventListener('ended', handleTrackEndedRef.current)
      track.stop()
    }
  }

  const handleTrackEndedRef = useRef<() => void>(() => {})

  const attach = (next: MediaStream) => {
    releaseStream()
    streamRef.current = next
    for (const track of next.getTracks()) {
      track.enabled = enabledRef.current
      track.addEventListener('ended', handleTrackEndedRef.current)
    }
    retryAttemptRef.current = 0
    setStream(next)
    setState('active')
  }

  const scheduleRetry = () => {
    clearRetryTimer()
    const attempt = retryAttemptRef.current
    const delay = RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)]
    retryAttemptRef.current = attempt + 1
    const generation = generationRef.current
    retryTimerRef.current = setTimeout(async () => {
      retryTimerRef.current = null
      try {
        const next = await getUserMedia()
        if (generation !== generationRef.current) {
          for (const track of next.getTracks()) track.stop()
          return
        }
        attach(next)
      } catch (error) {
        if (generation !== generationRef.current) return
        if (isNotAllowed(error)) {
          // Permission revoked: no retry will succeed until the user changes it.
          releaseStream()
          setStream(null)
          setState('denied')
          return
        }
        scheduleRetry()
      }
    }, delay)
  }

  handleTrackEndedRef.current = () => {
    // Stopping our own tracks in `stop()` never reaches here (the listener is removed
    // first), so an `ended` event is always the device going away under us.
    if (!streamRef.current) return
    releaseStream()
    setStream(null)
    setState('lost')
    retryAttemptRef.current = 0
    scheduleRetry()
  }

  const request = useCallback(async (): Promise<'active' | 'denied'> => {
    if (streamRef.current) return 'active'
    clearRetryTimer()
    const generation = generationRef.current
    setState('requesting')
    try {
      const next = await getUserMedia()
      if (generation !== generationRef.current) {
        for (const track of next.getTracks()) track.stop()
        return 'denied'
      }
      attach(next)
      return 'active'
    } catch (error) {
      if (generation !== generationRef.current) return 'denied'
      console.warn('[SelfCamera] Camera request refused:', error)
      setState('denied')
      return 'denied'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setEnabled = useCallback((enabled: boolean) => {
    enabledRef.current = enabled
    for (const track of streamRef.current?.getVideoTracks() ?? []) {
      track.enabled = enabled
    }
    setIsEnabled(enabled)
  }, [])

  const stop = useCallback(() => {
    generationRef.current += 1
    clearRetryTimer()
    retryAttemptRef.current = 0
    releaseStream()
    enabledRef.current = true
    setStream(null)
    setIsEnabled(true)
    setState('idle')
  }, [])

  useEffect(() => stop, [stop])

  return { state, stream, isEnabled, request, setEnabled, stop }
}

export default useSelfCamera
